import React, { useState, useCallback, useMemo } from "react";
import {
  Plus,
  Trash2,
  Copy,
  Download,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Upload,
  Monitor,
  X,
  FileJson,
} from "lucide-react";
import { toast } from "react-toastify";
import { LIMITS } from "../../utils/flowConstants";
import { validateFlow } from "../../utils/flowValidation";
import {
  createBlankScreen,
  createStarterFlow,
  buildFlowJSON,
  exportFlowJSON,
  uid,
} from "../../utils/flowJsonBuilder";
import ScreenEditor from "./ScreenEditor";
import FlowPreview from "./FlowPreview";

const MetaFlowBuilder = ({ initialFlow, onSave, onBack, onChange }) => {
  const [flow, setFlowInternal] = useState(
    () => initialFlow || createStarterFlow()
  );
  const setFlow = useCallback((updater) => {
    setFlowInternal((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (onChange) onChange(next);
      return next;
    });
  }, [onChange]);
  const [activeScreenIdx, setActiveScreenIdx] = useState(0);
  const [showValidation, setShowValidation] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const screens = flow.screens || [];
  const activeScreen = screens[activeScreenIdx] || null;

  const validation = useMemo(() => {
    const json = buildFlowJSON(flow);
    return validateFlow(json);
  }, [flow]);

  // ────── Screen operations ──────

  const addScreen = useCallback(() => {
    if (screens.length >= LIMITS.MAX_SCREENS) {
      toast.warning(`Maximum ${LIMITS.MAX_SCREENS} screens allowed`);
      return;
    }
    const existingIds = screens.map((s) => s.id);
    const newScreen = createBlankScreen(existingIds);
    setFlow((prev) => ({
      ...prev,
      screens: [...prev.screens, newScreen],
    }));
    setActiveScreenIdx(screens.length);
  }, [screens]);

  const deleteScreen = useCallback(
    (idx) => {
      if (screens.length <= LIMITS.MIN_SCREENS) {
        toast.warning("Flow must have at least 1 screen");
        return;
      }
      const screenId = screens[idx]?.id;
      const isNavTarget = screens.some((s) => {
        const children = s.layout?.children || [];
        return children.some((c) => {
          if (c.type === "Form") {
            return (c.children || []).some(
              (fc) => fc["on-click-action"]?.next?.name === screenId
            );
          }
          return c["on-click-action"]?.next?.name === screenId;
        });
      });
      if (isNavTarget) {
        toast.error(
          `Cannot delete "${screenId}" — other screens navigate to it. Update navigation first.`
        );
        return;
      }
      setFlow((prev) => ({
        ...prev,
        screens: prev.screens.filter((_, i) => i !== idx),
      }));
      setActiveScreenIdx((prev) => Math.min(prev, screens.length - 2));
    },
    [screens]
  );

  const duplicateScreen = useCallback(
    (idx) => {
      if (screens.length >= LIMITS.MAX_SCREENS) {
        toast.warning(`Maximum ${LIMITS.MAX_SCREENS} screens allowed`);
        return;
      }
      const original = screens[idx];
      const existingIds = screens.map((s) => s.id);
      let newId = `${original.id}_COPY`;
      let counter = 1;
      while (existingIds.includes(newId)) {
        newId = `${original.id}_COPY_${counter}`.slice(0, 30);
        counter++;
      }
      const dupe = JSON.parse(JSON.stringify(original));
      dupe.id = newId;
      dupe._uid = uid();
      dupe.title = `${original.title || ""} (Copy)`;
      setFlow((prev) => ({
        ...prev,
        screens: [...prev.screens, dupe],
      }));
      setActiveScreenIdx(screens.length);
    },
    [screens]
  );

  const updateScreen = useCallback(
    (updated) => {
      setFlow((prev) => ({
        ...prev,
        screens: prev.screens.map((s, i) =>
          i === activeScreenIdx ? updated : s
        ),
      }));
    },
    [activeScreenIdx]
  );

  // ────── Export ──────

  const copyJson = useCallback(() => {
    const { json, validation: v } = exportFlowJSON(flow);
    if (!v.valid) {
      toast.error("Fix validation errors before exporting");
      setShowValidation(true);
      return;
    }
    navigator.clipboard.writeText(JSON.stringify(json, null, 2));
    toast.success("JSON copied to clipboard");
  }, [flow]);

  const downloadJson = useCallback(() => {
    const { json, validation: v } = exportFlowJSON(flow);
    if (!v.valid) {
      toast.error("Fix validation errors before exporting");
      setShowValidation(true);
      return;
    }
    const blob = new Blob([JSON.stringify(json, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flow.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [flow]);

  const importJson = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target.result);
          if (!parsed.version || !parsed.screens) {
            toast.error("Invalid flow JSON structure");
            return;
          }
          // Add _uid to all nodes
          const addUids = (obj) => {
            if (Array.isArray(obj)) return obj.map(addUids);
            if (obj && typeof obj === "object") {
              const out = { ...obj, _uid: uid() };
              Object.keys(out).forEach((k) => {
                if (Array.isArray(out[k])) out[k] = out[k].map(addUids);
                else if (out[k] && typeof out[k] === "object" && k !== "on-click-action" && k !== "next")
                  out[k] = addUids(out[k]);
              });
              return out;
            }
            return obj;
          };
          const imported = addUids(parsed);
          setFlow(imported);
          setActiveScreenIdx(0);
          toast.success("Flow imported successfully");
        } catch {
          toast.error("Failed to parse JSON file");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  // ────── Render ──────

  const jsonSize = useMemo(() => {
    const str = JSON.stringify(buildFlowJSON(flow));
    return (new Blob([str]).size / 1024).toFixed(1);
  }, [flow]);

  return (
    <div className="min-h-screen bg-[#f8fafb]">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <div>
              <h1 className="text-sm font-bold text-gray-900">
                WhatsApp Flow Builder
              </h1>
              <p className="text-[10px] text-gray-500">
                {screens.length} screen{screens.length !== 1 ? "s" : ""} ·{" "}
                {jsonSize} KB
                {!validation.valid && (
                  <span className="text-red-500 ml-2">
                    · {validation.errors.length} error
                    {validation.errors.length !== 1 ? "s" : ""}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={importJson}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Upload size={13} /> Import
            </button>
            <button
              onClick={() => setShowValidation(!showValidation)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg transition-colors ${
                validation.valid
                  ? "text-green-600 bg-green-50 border border-green-200"
                  : "text-red-600 bg-red-50 border border-red-200"
              }`}
            >
              {validation.valid ? (
                <CheckCircle size={13} />
              ) : (
                <AlertCircle size={13} />
              )}
              {validation.valid ? "Valid" : "Errors"}
            </button>
            <button
              onClick={() => setShowJson(!showJson)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileJson size={13} /> JSON
            </button>
            <button
              onClick={copyJson}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Copy size={13} /> Copy
            </button>
            <button
              onClick={downloadJson}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-[#048AAD] rounded-lg hover:bg-[#037a9a] transition-colors"
            >
              <Download size={13} /> Download
            </button>
          </div>
        </div>
      </div>

      {/* Validation panel */}
      {showValidation && (
        <div className="bg-white border-b border-gray-200 px-5 py-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-700">Validation</h3>
            <button
              onClick={() => setShowValidation(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          </div>
          {validation.errors.length > 0 && (
            <div className="space-y-1 mb-2">
              {validation.errors.map((err, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <AlertCircle
                    size={12}
                    className="text-red-500 shrink-0 mt-0.5"
                  />
                  <p className="text-[11px] text-red-600">{err}</p>
                </div>
              ))}
            </div>
          )}
          {validation.warnings.length > 0 && (
            <div className="space-y-1">
              {validation.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <AlertCircle
                    size={12}
                    className="text-amber-500 shrink-0 mt-0.5"
                  />
                  <p className="text-[11px] text-amber-600">{w}</p>
                </div>
              ))}
            </div>
          )}
          {validation.valid && validation.warnings.length === 0 && (
            <p className="text-[11px] text-green-600 flex items-center gap-1.5">
              <CheckCircle size={12} /> Flow is valid and ready to export
            </p>
          )}
        </div>
      )}

      {/* JSON viewer */}
      {showJson && (
        <div className="bg-gray-900 border-b border-gray-700 px-5 py-3 max-h-64 overflow-y-auto">
          <pre className="text-[10px] text-green-400 font-mono whitespace-pre-wrap">
            {JSON.stringify(buildFlowJSON(flow), null, 2)}
          </pre>
        </div>
      )}

      {/* Main content */}
      <div className="flex" style={{ height: "calc(100vh - 120px)" }}>
        {/* Screen navigator (left sidebar) */}
        <div className="w-48 bg-white border-r border-gray-200 flex flex-col shrink-0">
          <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
              Screens
            </span>
            <button
              onClick={addScreen}
              disabled={screens.length >= LIMITS.MAX_SCREENS}
              className="p-1 text-[#048AAD] hover:bg-[#048AAD]/10 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Add screen"
            >
              <Plus size={14} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {screens.map((s, i) => (
              <div
                key={s._uid || s.id}
                onClick={() => setActiveScreenIdx(i)}
                className={`px-3 py-2.5 cursor-pointer border-b border-gray-50 transition-colors group ${
                  i === activeScreenIdx
                    ? "bg-[#048AAD]/5 border-l-2 border-l-[#048AAD]"
                    : "hover:bg-gray-50 border-l-2 border-l-transparent"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-gray-800 truncate">
                      {s.title || s.id}
                    </p>
                    <p className="text-[9px] text-gray-400 font-mono truncate">
                      {s.id}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateScreen(i);
                      }}
                      className="p-0.5 text-gray-400 hover:text-[#048AAD]"
                      title="Duplicate"
                    >
                      <Copy size={11} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteScreen(i);
                      }}
                      className="p-0.5 text-gray-400 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
                {s.terminal && (
                  <span className="text-[8px] font-semibold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded mt-1 inline-block">
                    TERMINAL
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="px-3 py-2 text-[9px] text-gray-400 border-t border-gray-100">
            {screens.length}/{LIMITS.MAX_SCREENS} screens
          </div>
        </div>

        {/* Screen editor (center) */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeScreen ? (
            <ScreenEditor
              screen={activeScreen}
              screens={screens}
              onUpdate={updateScreen}
            />
          ) : (
            <div className="text-center py-20 text-gray-400 text-sm">
              Select a screen or create a new one
            </div>
          )}
        </div>

        {/* Preview (right) */}
        <div className="w-[360px] bg-gray-50 border-l border-gray-200 p-4 overflow-y-auto shrink-0">
          <div className="flex items-center gap-2 mb-3">
            <Monitor size={13} className="text-gray-400" />
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
              Live Preview
            </span>
          </div>
          <FlowPreview flow={flow} />
        </div>
      </div>
    </div>
  );
};

export default MetaFlowBuilder;
